import { useMemo } from 'react';
import { INumberDictionary, IStore } from '../../interfaces';
import { IAvailableCompetition, ICompetitionEvolution, ISelectedEvolutions, ITeam } from '../../interfaces/clientInterfaces';
import { useSelector } from 'react-redux';
import { Countries } from '../../utils/countries';
import * as actions from '../../actions';
import { useAppDispatch } from '../../utils/modules';
import styles from './Editions.module.css';

interface IEditionsInjectedProps {
	teamsById: INumberDictionary<ITeam>;
	evolutions: INumberDictionary<ICompetitionEvolution>; // Evolutions by competition
	availableCompetitions: IAvailableCompetition[];
	selectedEvolutions: ISelectedEvolutions[];
	isMobileViewActive: boolean;
}

interface IEditionCell {
	lastChampion: IEditionCellChampion;
	lastSharedChampions: IEditionCellChampion[];
	lastRunnerUpId: number;
	description: string;
	endYear: number;
	incidences: string;
}

interface IEditionCellChampion {
	id: number;
	timesChampion: number;
}

export default function Editions() {
	const dispatch = useAppDispatch();

	const {
		teamsById,
		evolutions,
		availableCompetitions,
		selectedEvolutions,
		isMobileViewActive,
	} = useSelector(
		(completeState: IStore): IEditionsInjectedProps => {
			return {
				teamsById: completeState.teams.teamsById,
				availableCompetitions: completeState.competitions.availableCompetitions,
				evolutions: completeState.competitions.evolutions,
				selectedEvolutions: completeState.competitions.selectedEvolutions,
				isMobileViewActive: completeState.ui.isMobileViewActive
			}
		});

	const { editions, competition } = useMemo(
		(): {
			editions: IEditionCell[];
			competition: IAvailableCompetition | null;
		 } => {
			if (selectedEvolutions.length < 1) {
				return {
					editions: [],
					competition: null,
				};
			}
			const competitionName = selectedEvolutions[0].competitionName;
			const competition = availableCompetitions.find(competition => competition.name === competitionName);
			const editions: IEditionCell[] = [];
			if (!competition) {
				return {
					editions,
					competition: null,
				};
			}
			const evolution = evolutions[competition.id];
			if (!evolution) {
				return {
					editions,
					competition: null,
				};
			}
			const timesChampion = new Map<number, number>();
			for (const edition of evolution.editions) {
				
				let timesChampionUntilEdition = timesChampion.get(edition.lastChampionId);
				if (!timesChampionUntilEdition) {
					timesChampionUntilEdition = 0;
				}
				timesChampion.set(edition.lastChampionId, timesChampionUntilEdition + 1);
				if (edition.incidences?.indexOf('Shared') > -1 && editions.length > 0) {
					const previousEdition = editions.shift();
					if (previousEdition) {
						editions.unshift({
							...previousEdition,
							lastSharedChampions: [...previousEdition.lastSharedChampions, {
								id: edition.lastChampionId,
								timesChampion: timesChampionUntilEdition + 1
							}],
							incidences: edition.incidences
						});
					}
				} else {
					editions.unshift({
						lastChampion: {
							id: edition.lastChampionId,
							timesChampion: timesChampionUntilEdition + 1
						},
						lastSharedChampions: [],
						lastRunnerUpId: edition.lastRunnerUpId,
						description: edition.editionName,
						endYear: edition.endYear,
						incidences: edition.incidences
					});
				}
			}
			return {
				editions,
				competition,
			};
		},
		[selectedEvolutions, evolutions, availableCompetitions]
	);

	const renderFlag = (year: number, team: ITeam | undefined, competition: IAvailableCompetition | null) => {
		if (!team || !competition) {
			return null;
		}
		if (competition.regionCode === team.countryCode || competition.regionCode === 'DR' || competition.regionCode === 'ZK') {
			return null;
		}
		return (
			<img
				className={styles.editionsFlagImage}
				style={{
					width: team.teamCode ? '30px' : '35px'
				}}
				width={team.teamCode ? 30: 35}
				src={`../../../img/flag/${Countries.getFlagOfCountry(year, competition.regionCode, team.countryCode, team.countryCode2, !team.teamCode)}.png`}
				alt={competition.regionCode}
			/>
		);
	}

	const renderTeam = (year: number, team: ITeam | undefined, competition: IAvailableCompetition | null, timesChampion: number | null) => {
		if (!team) {
			return null;
		}
		const teamShield = [...team.shields].reverse().find((shield: string) => Number.parseInt(shield) >= year);
		const shieldCode = `${team.countryCode}_${team.teamCode}${teamShield ? `_${teamShield}` : ''}`;
		return (
			<>
				<span className={styles.editionsShieldAndFlagContainer} onClick={e => dispatch(actions.setHomeDetailTeam(team.teamId))}>
					{renderFlag(year, team, competition)}
					{
						team.teamCode
							? <img className={styles.editionsShieldImage} src={`../../../img/shield/${shieldCode}.png`} alt={team.name} />
							: null
					}
					{!isMobileViewActive && <div className={styles.editionsNameContainer}>
						<div>{team.name}</div>
						{timesChampion !== null && <div className={styles.timesChampionContainer}>{`(${timesChampion})`}</div>}
					</div>}
				</span>
				{isMobileViewActive &&
					<div className={styles.editionsNameContainer} onClick={e => dispatch(actions.setHomeDetailTeam(team.teamId))}>
						<div className={styles.editionsTeamName}>{team.shortName || team.name}</div>
						{timesChampion !== null && <div className={styles.timesChampionContainer}>{`(${timesChampion})`}</div>}
					</div>
				}
				<span className={styles.editionsScarfContainer} onClick={e => dispatch(actions.setHomeDetailTeam(team.teamId))}>
					<img
						className={styles.editionsScarfImage}
						src={`../../../img/scarf/${team.countryCode}_${team.teamCode}.jpg`}
						style={{
							width: isMobileViewActive ? '120px' : '200px'
						}}
						alt={team.name}
					/>
				</span>
			</>
			
		);
	}

	return (
		<div
			className={isMobileViewActive ? styles.mobileEditionsContainer : styles.editionsContainer}
		>
			<table
				className={styles.editionsTable}
				style={{
					fontSize: isMobileViewActive ? '12px' : '16px'
				}}
			>
				<thead>
					<th className={styles.edtionsYearCellHeader} scope='column'>
						{'YEAR'}
					</th>
					<th className={styles.editionsTeamCellHeader} scope='column'>
						{'CHAMPION'}
					</th>
					<th className={styles.editionsTeamCellHeader} scope='column'>
						{'RUNNER UP'}
					</th>
				</thead>
				<tbody>
					{editions.map(edition => {
						return (
							<>
								<tr>
									<td rowSpan={2} className={styles.edtionsYearCellHeader}>
										{edition.description}
									</td>
									<th className={styles.editionsTeamCellHeader}>
										{renderTeam(edition.endYear, teamsById[edition.lastChampion.id], competition, edition.lastChampion.timesChampion)}
										{
											edition.lastSharedChampions.length > 0
												? edition.lastSharedChampions.map(champion => renderTeam(edition.endYear, teamsById[champion.id], competition, champion.timesChampion))
												: null
										}
									</th>
									<th className={styles.editionsTeamCellHeader}>
										{renderTeam(edition.endYear, teamsById[edition.lastRunnerUpId], competition, null)}
										
									</th>
								</tr>
								<tr className={styles.incidencesRow}>
									<td colSpan={2} className={styles.incidencesContainer}>
										{edition.incidences}
									</td>
								</tr>
							</>
						);
					})}
				</tbody>
			</table>
		</div>
	);
}

